import { faEdit } from '@fortawesome/free-solid-svg-icons'
import { TFunction } from 'i18next'
import React, { FC, useContext, useEffect, useMemo, useState } from 'react'
import { Nav, Tab } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import AppConfig from '../../../adapter/AppConfig'
import Authorization from '../../Authentication/Authorization'
import DialogContext from '../../Authentication/DialogContext'
import DataTable from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import Loading from '../../layout/Loading'
import AppService from '../../service/AppService'
import { getDataField } from '../../utils/DialogUtil'

const AppDetail: FC<{ props: any }> = ({ props }) => {
  const { t } = useTranslation()
  const [app, setApp] = useState({} as any)
  const [appDefinition, setAppDefinition] = useState({} as any)
  const [loading, setLoading] = useState(true)
  const { editDialogPropsSetter } = useContext(DialogContext)

  const appId: number = props.appId

  useEffect(() => {
    async function loadData() {
      const appDetail = await AppService.getByID(appId)
      const definition = await AppService.getDefinitionByType(appDetail.type)
      setApp(appDetail)
      setAppDefinition(definition)
    }
    loadData().finally(() => setLoading(false))
  }, [appId])

  const columns = useMemo(() => {
    return [
      {
        text: 'label',
        dataField: 'label',
        classes: 'font-weight-bolder',
        sort: true,
      },
      {
        text: 'value',
        dataField: 'value',
        sort: true,
      },
    ]
  }, [])

  const data = appDefinition.attributes
    ? appDefinition.attributes.map((attr: any) => {
        return {
          label: attr.label,
          value: attr.type === 'PASSWORD' ? '........' : app[attr.name],
        }
      })
    : []

  return (
    <Loading isLoading={loading}>
      <Tab.Container id='appdetail-tab' defaultActiveKey='home'>
        <Nav variant='tabs'>
          <Nav.Item>
            <Nav.Link eventKey='home'>Home</Nav.Link>
          </Nav.Item>
          {AppConfig[app.type]?.map((tab: any) => (
            <Nav.Item key={tab.eventKey}>
              <Nav.Link eventKey={tab.eventKey}>{tab.title}</Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey='home'>
            <DataTable
              keyField='label'
              data={data}
              columns={columns}
              caption={appDefinition.name}
              headerClasses='d-none'
              searchable={false}
              captionButtons={[
                {
                  onClick: () => editAppDialog(t, editDialogPropsSetter, app, setApp, appDefinition),
                  text: t('edit'),
                  icon: faEdit,
                  permissions: Authorization.apps.edit,
                },
              ]}
            />
          </Tab.Pane>
          {AppConfig[app.type]?.map((tab: any) => (
            <Tab.Pane eventKey={tab.eventKey} key={tab.eventKey}>
              <tab.Component appId={appId} />
            </Tab.Pane>
          ))}
        </Tab.Content>
      </Tab.Container>
    </Loading>
  )
}

function editAppDialog(t: TFunction, editDialogPropsSetter: any, app: any, setApp: React.Dispatch<any>, appDefinition: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('app.editHeader'),
    onSubmit: AppService.updateApp,
    onSubmitExtraArgs: [t, setApp],
    data: (custom: string[]) => getEditData(app, appDefinition, custom[0]),
    initCustom: [app.disableIndexing],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

function getEditData(app: any, appDefinition: any, custom: any): any[] {
  var data = appDefinition.attributes
    .map((attribute: any) => {
      attribute.value = app[attribute.name]
      if (attribute.name === 'type' || attribute.name === 'name') {
        attribute.plaintext = true
        attribute.readOnly = true
      }
      if (custom && attribute.name === 'collectionName') {
        return {}
      }
      if (attribute.name === 'disableIndexing') {
        attribute.value = custom
        attribute.onChange = (e: any, setCustom: React.Dispatch<any>) => setCustom([e])
      }
      return getDataField(attribute)
    })
    .filter((val: any) => Object.keys(val).length > 0)

  data.push({
    id: 'id',
    value: app.id,
    type: 'hidden',
    render: false,
  })

  return data
}

export default AppDetail
